import { useEffect, useRef, useCallback } from 'react'

// Performance monitoring hook
export const usePerformanceMonitor = (componentName) => {
  const renderStartTime = useRef(performance.now())
  const mountTime = useRef(null)
  
  useEffect(() => {
    // Component mounted
    mountTime.current = performance.now()
    const mountDuration = mountTime.current - renderStartTime.current
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} mounted in ${mountDuration.toFixed(2)}ms`)
    }
    
    return () => {
      // Component unmounted
      const unmountTime = performance.now()
      const totalLifetime = unmountTime - mountTime.current
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`${componentName} unmounted after ${totalLifetime.toFixed(2)}ms`)
      }
    }
  }, [componentName])
  
  const measureOperation = useCallback((operationName, operation) => {
    const startTime = performance.now()
    const result = operation()
    const endTime = performance.now()
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} - ${operationName}: ${(endTime - startTime).toFixed(2)}ms`)
    }
    
    return result
  }, [componentName])
  
  const measureAsyncOperation = useCallback(async (operationName, operation) => {
    const startTime = performance.now()
    const result = await operation()
    const endTime = performance.now()
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} - ${operationName}: ${(endTime - startTime).toFixed(2)}ms`)
    }
    
    return result
  }, [componentName])
  
  return { measureOperation, measureAsyncOperation }
}

// Web Vitals monitoring
export const useWebVitals = () => {
  useEffect(() => {
    if (typeof window !== 'undefined' && 'web-vitals' in window) {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log)
        getFID(console.log)
        getFCP(console.log)
        getLCP(console.log)
        getTTFB(console.log)
      })
    }
  }, [])
}

// Memory usage monitoring
export const useMemoryMonitor = (intervalMs = 30000) => {
  useEffect(() => {
    if (!performance.memory || process.env.NODE_ENV !== 'development') return
    
    const logMemoryUsage = () => {
      const memory = performance.memory
      console.log('Memory Usage:', {
        used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
        limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
      })
    }
    
    const interval = setInterval(logMemoryUsage, intervalMs)
    
    return () => clearInterval(interval)
  }, [intervalMs])
}

// Bundle size analyzer
export const useBundleAnalyzer = () => {
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Log loaded modules
      const loadedModules = Object.keys(window.webpackChunkName || {})
      console.log('Loaded modules:', loadedModules.length)
      
      // Monitor chunk loading
      if (window.__webpack_require__) {
        const originalRequire = window.__webpack_require__
        window.__webpack_require__ = function(moduleId) {
          const startTime = performance.now()
          const result = originalRequire.call(this, moduleId)
          const endTime = performance.now()
          
          console.log(`Module ${moduleId} loaded in ${(endTime - startTime).toFixed(2)}ms`)
          return result
        }
      }
    }
  }, [])
}

export default usePerformanceMonitor
